// assets/kivivalinta.js
import { Component } from '@theme/component';

/**
 * @typedef {object} KivivalintaRefs
 * @property {HTMLSelectElement[]} selects - Piilotetut lomakekentät, yksi per valinta.
 * @property {HTMLButtonElement[]} toggles - Avauspainikkeet.
 * @property {HTMLUListElement[]} lists - Kivilistat.
 * @property {HTMLElement[]} thumbs - Valitun kiven kuvapaikka.
 * @property {HTMLElement[]} names - Valitun kiven nimi.
 */

/**
 * Kivivalinta tuotesivulla. Kuvallinen käyttöliittymä peilaa oikeita select-kenttiä,
 * jotka hoitavat lomakkeen ja pakollisuuden. Ilman JS:ää selectit jäävät näkyviin
 * ja tuote toimii normaalisti.
 * @extends Component<KivivalintaRefs>
 */
class Kivivalinta extends Component {
  connectedCallback() {
    super.connectedCallback?.();
    this.#enhance();
    this.#onDocumentClick = this.#handleDocumentClick.bind(this);
    document.addEventListener('click', this.#onDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('click', this.#onDocumentClick);
  }

  #onDocumentClick;

  /** Piilottaa selectit ja paljastaa kuvallisen valinnan. */
  #enhance() {
    for (const slot of this.#slots()) {
      const select = slot.querySelector('.kivivalinta__select');
      const ui = slot.querySelector('.kivivalinta__ui');
      if (!select || !ui) continue;
      select.classList.add('visually-hidden');
      ui.hidden = false;
      this.#syncFromSelect(slot);
    }
  }

  #slots() {
    return this.querySelectorAll('.kivivalinta__slot');
  }

  handleToggleClick(event) {
    // Horizon kuuntelee dokumentin tasolla, joten currentTarget on document.
    // Oikea elementti tulee proxyn kautta event.target-kentässä.
    const toggle = event.target;
    const slot = toggle.closest('.kivivalinta__slot');
    const list = slot?.querySelector('.kivivalinta__list');
    if (!list) return;

    const open = toggle.getAttribute('aria-expanded') === 'true';
    this.#closeAll();
    if (!open) {
      toggle.setAttribute('aria-expanded', 'true');
      list.hidden = false;
    }
  }

  handleOptionClick(event) {
    const option = event.target;
    const slot = option.closest('.kivivalinta__slot');
    const select = slot?.querySelector('.kivivalinta__select');
    if (!select) return;

    select.value = option.dataset.value ?? '';
    select.dispatchEvent(new Event('change', { bubbles: true }));
    this.#closeAll();
    slot.querySelector('.kivivalinta__toggle')?.focus();
  }

  handleSelectChange(event) {
    const slot = event.target.closest('.kivivalinta__slot');
    if (slot) this.#syncFromSelect(slot);
  }

  /** Avaa valitun kiven tarinan teeman omalla dialogilla. */
  handleStoryClick(event) {
    const dialogId = event.target.dataset.dialog;
    if (!dialogId) return;

    const dialog = document.getElementById(dialogId);
    if (dialog && typeof dialog.showDialog === 'function') dialog.showDialog();
  }

  /** Vie selectin arvon painikkeeseen ja korostaa valitun rivin. */
  #syncFromSelect(slot) {
    const select = slot.querySelector('.kivivalinta__select');
    const name = slot.querySelector('.kivivalinta__name');
    const thumb = slot.querySelector('.kivivalinta__toggle .kivivalinta__thumb');
    if (!select || !name || !thumb) return;

    const value = select.value;
    const option = slot.querySelector(`.kivivalinta__option[data-value="${CSS.escape(value)}"]`);

    for (const item of slot.querySelectorAll('.kivivalinta__option')) {
      item.setAttribute('aria-selected', item === option ? 'true' : 'false');
    }

    name.textContent = option?.dataset.label || option?.textContent.trim() || select.options[select.selectedIndex]?.text || '';

    const storyLink = slot.querySelector('.kivivalinta__story-link');
    if (storyLink) {
      const dialogId = option?.dataset.dialog ?? '';
      storyLink.dataset.dialog = dialogId;
      storyLink.hidden = !dialogId;
    }

    const image = option?.dataset.image;
    if (image) {
      thumb.classList.remove('kivivalinta__thumb--empty');
      thumb.innerHTML = `<img src="${image}" alt="" loading="lazy">`;
    } else {
      thumb.classList.add('kivivalinta__thumb--empty');
      thumb.innerHTML = '';
    }
  }

  #closeAll() {
    for (const toggle of this.querySelectorAll('.kivivalinta__toggle')) {
      toggle.setAttribute('aria-expanded', 'false');
    }
    for (const list of this.querySelectorAll('.kivivalinta__list')) {
      list.hidden = true;
    }
  }

  #handleDocumentClick(event) {
    if (!this.contains(event.target)) this.#closeAll();
  }
}

customElements.define('kivivalinta-component', Kivivalinta);
